export type BoqLineLike = Record<string, unknown>;

const normalizePart = (value: unknown): string => String(value ?? "").trim();

/** productCode | product_code | product_id | si_code */
export const resolveBoqProductCode = (row: BoqLineLike): string =>
  normalizePart(
    row.productCode ??
      row.product_code ??
      row.product_id ??
      row.si_code
  );

/** productTitle | product_name | product_description | product_title */
export const resolveBoqProductTitle = (row: BoqLineLike): string =>
  normalizePart(
    row.productTitle ??
      row.product_name ??
      row.product_description ??
      row.product_title
  );

/** workItem | work_item (including nested catalog `data.work_item`) */
export const resolveBoqWorkItem = (row: BoqLineLike): string => {
  const nested = row.data as BoqLineLike | undefined;
  return normalizePart(row.workItem ?? row.work_item ?? nested?.work_item);
};

/** Partial key for matching sale/cart rows to a catalog/accessory product. */
export const boqLineKeyFromCatalogProduct = (product: BoqLineLike): BoqLineLike => ({
  product_id: resolveBoqProductCode(product),
  productTitle: resolveBoqProductTitle(product),
  workItem: resolveBoqWorkItem(product),
});

/** Same BoQ line (duplicate SI codes) — all three dimensions must align. */
export const isSameBoqProductLine = (left: BoqLineLike, right: BoqLineLike): boolean =>
  resolveBoqProductCode(left) === resolveBoqProductCode(right) &&
  resolveBoqProductTitle(left).toLowerCase() ===
    resolveBoqProductTitle(right).toLowerCase() &&
  resolveBoqWorkItem(left).toLowerCase() ===
    resolveBoqWorkItem(right).toLowerCase();

/** Match a sale/cart row to a partial key (e.g. boQDeleteKey or qty payload). */
export const rowMatchesBoqLineKey = (row: BoqLineLike, key: BoqLineLike): boolean => {
  const keyLine: BoqLineLike = {
    productCode: key.product_id ?? key.productCode ?? key.product_code,
    // cart rows overwrite product_name with the work item, so it is not a reliable title
    productTitle: key.productTitle ?? key.product_description ?? key.product_name,
    workItem: key.workItem ?? key.work_item,
  };
  if (resolveBoqProductCode(row) !== resolveBoqProductCode(keyLine)) {
    return false;
  }
  const keyTitle = resolveBoqProductTitle(keyLine);
  const rowTitle = resolveBoqProductTitle(row);
  if (keyTitle && rowTitle.toLowerCase() !== keyTitle.toLowerCase()) {
    return false;
  }
  const keyWork = resolveBoqWorkItem(keyLine);
  const rowWork = resolveBoqWorkItem(row);
  if (keyWork && rowWork.toLowerCase() !== keyWork.toLowerCase()) {
    return false;
  }
  return true;
};

/**
 * Index of the row for `key`. A unique product code matches on code alone;
 * title / work item are only used to pick between duplicate-code rows.
 */
export const findBoqLineIndex = (rows: BoqLineLike[], key: BoqLineLike): number => {
  const code = resolveBoqProductCode({
    productCode: key.product_id ?? key.productCode ?? key.product_code,
  });
  const sameCodeIndexes: number[] = [];
  rows.forEach((row, index) => {
    if (resolveBoqProductCode(row) === code) {
      sameCodeIndexes.push(index);
    }
  });
  if (sameCodeIndexes.length <= 1) {
    return sameCodeIndexes.length ? sameCodeIndexes[0] : -1;
  }
  const exactIndex = sameCodeIndexes.find((index) => rowMatchesBoqLineKey(rows[index], key));
  return exactIndex ?? -1;
};
